import { Box, Chip, Divider, Grid, Typography } from "@mui/material";
import { useFormik } from "formik";
import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import Flex from "../../../../../../components/Flex";
import Input from "../../../../../../components/Input";
import OutlineCard from "../../../../../../components/OutlineCard";
import Text from "../../../../../../components/Text";
import ConfirmationModal from "../../../../../../components/app/ConfirmationModal";
import StyledButtonV2 from "../../../../../../components/app/StyledButtonV2";
import useDisclosure from "../../../../../../hooks/use-disclosure";
import { useSnackbar } from "../../../../../../hooks/useSnackbar";
import { useGetConfigurationDetailsByName } from "../../../../../../queries/practice-setting/configuration/use-get-config-details-by-name";
import { useAddPatientIdConfig } from "../../../../../../queries/practice-setting/patient-id-configuration/use-add-patient-id-config";
import {
  getXtenantId,
  showToastOnApiCall,
} from "../../../../../../utils/utils";
import { PatientIdConfig, initialValues } from "./patient-id-config-constant";
import { PRIMITIVE } from "../../../../../../tokens/design-tokens";

const PatientIdConfiguration: React.FC = () => {
  const { mutateAsync: addPatientIdConfig } = useAddPatientIdConfig();
  const showSnackbar = useSnackbar();
  const params = useParams();
  const tenantName = getXtenantId() ?? params?.providerGroupId ?? "";
  const { data: configurationDetails, refetch } =
    useGetConfigurationDetailsByName({
      providerGroupName: tenantName,
    });

  useEffect(() => {
    if (configurationDetails?.data) {
      const prefillValues = getPrefillPatientIdConfigFields(
        (configurationDetails?.data as any)?.ecarehealthPatientId as string
      );
      formik.setValues(prefillValues as any);
    }
  }, [configurationDetails?.data]);

  const handleAddPatientIdConfig = async () => {
    showToastOnApiCall({
      apiCall: addPatientIdConfig({
        ecarehealthPatientId: generatePreview(),
        xTENANTID: tenantName,
      }),
      showSnackbar,
      onSave: () => {
        refetch();
        confirmationDisc.close();
      },
    });
  };

  const formik = useFormik<PatientIdConfig>({
    initialValues,
    // validationSchema,
    onSubmit: handleAddPatientIdConfig,
  });

  // Generate preview of patient ID
  const generatePreview = (): string => {
    const { prefix, serialNumberStart } = formik.values;
    return `${prefix?.padStart(2, "0")}${serialNumberStart?.padStart(3, "0")}`;
  };

  const handleInputChange =
    (field: keyof PatientIdConfig) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      let value = event.target.value;

      if (field === "serialNumberStart") {
        // Only allow numeric input
        value = value.replace(/\D/g, "");

        // Ensure minimum length with leading zeros for display
        value = value.substring(0, 4);
      }

      // if (field === "suffix") {
      //   value = value.replace(/[^A-Za-z]/g, "").toUpperCase();

      //   if (value.length > 2) value = value.substring(0, 2);
      // }

      // Update formik values
      formik.setFieldValue(field, value);
    };

  const enabled =
    (configurationDetails?.data as any)?.ecarehealthPatientId === "EC0000";

  const confirmationDisc = useDisclosure();

  return (
    <OutlineCard sx={{ height: "100%" }}>
      <Flex
        justifyContent="space-between"
        alignItems="center"
        marginBottom="20px"
      >
        <Text fontSize="16px" fontWeight="600">
          Patient ID Configuration
        </Text>
      </Flex>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Configure how patient IDs are generated for your practice. This setting
        will apply to all new patients.
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        <b>Warning:</b> This action is irreversible.
      </Typography>

      <ConfirmationModal
        maxWidth={"sm"}
        onConfirm={() => {
          formik.handleSubmit();
        }}
        disclosureHost={confirmationDisc}
        confirmationText="Yes"
        cancelText="No"
        title={
          <Text fontWeight={"medium"} fontSize={"24px"}>
            Are you sure? You won't be able to change this later
          </Text>
        }
      />

      <form onSubmit={formik.handleSubmit}>
        <Text mb={2}>
          Configure the parameters below to customize how patient IDs are
          generated. The preview will show you how the generated IDs will look.
        </Text>

        <Grid container spacing={3}>
          {/* Prefix Field */}
          <Grid item xs={6}>
            <Input
              label="Prefix"
              // required
              textFieldProps={{
                name: "prefix",
                disabled: !enabled,
                value: formik.values.prefix,
                onChange: formik.handleChange,
                onBlur: formik.handleBlur,
                style: { backgroundColor: "#F5F5F5" },
              }}
              // error={formik.touched.prefix && formik.errors.prefix}
            />
          </Grid>
          {/* Serial Number Start Field */}
          <Grid item xs={6}>
            <Input
              label="Sr.No Start"
              // required
              textFieldProps={{
                name: "serialNumberStart",
                disabled: !enabled,
                value: formik.values.serialNumberStart,
                onChange: handleInputChange("serialNumberStart"),
                onBlur: formik.handleBlur,
                inputProps: {
                  maxLength: 10,
                  pattern: "[0-9]*",
                },
              }}
              // error={
              //   formik.touched.serialNumberStart &&
              //   formik.errors.serialNumberStart
              // }
            />
          </Grid>

          {/* <Grid item xs={6} md={4}>
            <Input
              label="Suffix"
              // required
              textFieldProps={{
                name: "suffix",
                disabled: disabled,
                value: formik.values.suffix,
                onChange: handleInputChange("suffix"),
                onBlur: formik.handleBlur,
                inputProps: {
                  maxLength: 2,
                  style: { textTransform: "uppercase" },
                },
              }}
              // error={formik.touched.suffix && formik.errors.suffix}
            />
          </Grid> */}
        </Grid>

        <Divider sx={{ my: 1 }} />

        {/* Preview Section */}
        <Box>
          {/* <Text fontWeight={"bold"} fontSize={"20px"} gutterBottom>
            Generated Patient ID Format
          </Text> */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Preview:
            </Typography>
            <PreviewGeneratedPatientId id={generatePreview()} />
            {/* <Text>{` [${formik.values.prefix}]  [${formik.values.serialNumberStart}]  [${formik.values.suffix}]`}</Text> */}
          </Box>
          <Typography variant="body2" color="text.secondary">
            Examples: {generatePreview()},{" "}
            {generatePreview().replace(/\d+/, (match) =>
              String(parseInt(match) + 1).padStart(match.length, "0")
            )}
            ,{" "}
            {generatePreview().replace(/\d+/, (match) =>
              String(parseInt(match) + 2).padStart(match.length, "0")
            )}
            ...
          </Typography>
        </Box>

        <Divider sx={{ mb: 1 }} />

        {/* Action Buttons */}
        <Flex width={"100%"} justifyContent={"end"}>
          <StyledButtonV2
            variant="contained"
            disabled={!formik.isValid || !enabled}
            label="Save"
            onClick={() => confirmationDisc.open()}
          />
        </Flex>
      </form>
    </OutlineCard>
  );
};

export default PatientIdConfiguration;

const PreviewGeneratedPatientId = ({ id }: { id: string }) => (
  <Chip
    label={id}
    // color="primary"
    variant="filled"
    sx={{
      fontSize: "12px",
      fontWeight: "500",
      fontFamily: "monospace",
      backgroundColor: PRIMITIVE.color.lightBlue[50],
      color: "#004AB1",
      border: "1px solid #004AB1",
      height: "18px",
    }}
  />
);

const getPrefillPatientIdConfigFields = (id: string) => {
  return {
    prefix: id.substring(0, 2),
    serialNumberStart: id.substring(2, 6),
    // suffix: id.substring(5, 8),
  };
};
